import React from 'react';
import { useFormatter } from 'src/services/i18n';
import { CommentResponse, SystemEventType } from 'src/api/services/CommentClient';
import { RequestStatusBadge } from 'src/features/requests/request-status-badge';

type Props = {
  comment: CommentResponse;
};

const ROW_CLASS = 'tw-flex tw-items-center tw-gap-3 tw-my-1.5 tw-px-2';
const CHIP_CLASS =
  'tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-text-subtle ' +
  'tw-whitespace-nowrap tw-border tw-border-neutral-200 ' +
  'tw-rounded-full tw-px-3 tw-py-1 tw-bg-white';
const BADGE_SLOT_CLASS =
  '[&>*]:!tw-py-0 [&>*]:!tw-leading-tight tw-leading-none tw-inline-flex tw-items-center';

/**
 * Renders an automated system message inside the Revisions chat thread.
 *
 * Layout: centered chip flanked by horizontal divider lines. Chip height
 * is uniform across all event types — the BADGE_SLOT_CLASS compresses
 * RequestStatusBadge's internal padding to match the surrounding text
 * line-height, so a status_changed pill is the same height as a plain
 * "Customer requested a re-edit" pill.
 *
 * Visible to editor / team-lead / admin (this repo). Editor names are
 * NOT masked here. The customer-frontend variant masks them as
 * "beCreatives team".
 */
const ChatSystemMessage: React.FC<Props> = ({ comment }) => {
  const { formatDateTime } = useFormatter();

  const eventType = (comment.system_event_type ?? 'status_changed') as SystemEventType;
  const data = (comment.system_event_data ?? {}) as Record<string, unknown>;
  const editorName = (data.editor_name as string | undefined) ?? 'Editor';
  const timestamp = formatDateTime(comment.created_at, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const renderBody = (): React.ReactNode => {
    switch (eventType) {
      case 'status_changed': {
        const to = (data.to as string | undefined) ?? '';
        return (
          <>
            <span>Status changed to</span>
            {to ? (
              <span className={BADGE_SLOT_CLASS}>
                <RequestStatusBadge size="sm" variant={to} />
              </span>
            ) : null}
          </>
        );
      }
      case 'reedit_requested':
        return <span>Customer requested a re-edit</span>;
      case 'reedit_reset':
        return <span>Customer cancelled the re-edit request</span>;
      case 'attention_requested':
        return <span>{editorName} flagged this for attention</span>;
      case 'approved':
        return <span>Customer approved</span>;
      case 'editor_assigned':
        return <span>{editorName} assigned to this request</span>;
      case 'file_uploaded': {
        const kind = data.kind as string | undefined;
        if (kind === 'supporting_materials') {
          return <span>Customer uploaded supporting materials</span>;
        }
        return <span>{editorName} uploaded a new revision</span>;
      }
      default:
        return null;
    }
  };

  const body = renderBody();
  if (body === null) {
    return null;
  }

  return (
    <div className={ROW_CLASS}>
      <div className="tw-flex-1 tw-h-px tw-bg-neutral-200" />
      <div className={CHIP_CLASS}>
        {body}
        <span>{timestamp}</span>
      </div>
      <div className="tw-flex-1 tw-h-px tw-bg-neutral-200" />
    </div>
  );
};

export default ChatSystemMessage;
